import { Hotel, Sun, Footprints, Check, ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";

const FEATURES = [
  {
    icon: Hotel,
    title: "Hundpensionat CMS",
    tagline: "Hotell för fyrfota gäster.",
    desc: "Allt du behöver för att driva ett professionellt hundpensionat — från ankomst till hemgång.",
    bullets: [
      "Rumstilldelning & beläggningskarta",
      "Diet- och medicinjournaler",
      "Digital incheckning med signatur",
    ],
    accent: "from-emerald-500 to-emerald-600",
    href: "/hundpensionat-system",
    testid: "feature-card-pensionat",
  },
  {
    icon: Sun,
    title: "Hunddagis App",
    tagline: "Det dagliga arbetet, automatiserat.",
    desc: "Spendera mindre tid på admin och mer tid med hundarna. Allt synkat med ägar-appen.",
    bullets: [
      "Närvarorapport med ett klick",
      "Automatisk månadsfakturering",
      "Bildflöde till hundägarna",
    ],
    accent: "from-slate-800 to-slate-900",
    href: "/hunddagis-app",
    testid: "feature-card-dagis",
  },
  {
    icon: Footprints,
    title: "Hundrastning & Schema",
    tagline: "Schemalägg, spåra, fakturera.",
    desc: "Hantera promenadteam, gps-rutter och fakturering — utan kalkylblad och whatsapp-kaos.",
    bullets: [
      "Live GPS-spårning av promenader",
      "Smart schemaläggning",
      "Hundvakts-roller & lön",
    ],
    accent: "from-emerald-600 to-teal-700",
    href: "/hundrastning-program",
    testid: "feature-card-walking",
  },
];

export default function Features() {
  return (
    <section id="features" data-testid="features-section" className="relative py-24 md:py-32">
      <div className="mx-auto max-w-7xl px-6 lg:px-12">
        <div className="max-w-3xl">
          <div className="text-xs tracking-[0.2em] uppercase font-semibold text-emerald-600 dark:text-emerald-400">
            Tre produkter — en plattform
          </div>
          <h2 className="font-display mt-4 text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-slate-900 dark:text-white">
            Allt en hundverksamhet behöver, på ett ställe.
          </h2>
          <p className="mt-5 text-lg text-slate-600 dark:text-slate-300 max-w-2xl">
            Hundify är byggt modulärt. Använd det som passar dig — eller ge dig själv superkrafter med hela paketet.
          </p>
        </div>

        <div className="mt-16 grid md:grid-cols-3 gap-6">
          {FEATURES.map(({ icon: Icon, title, tagline, desc, bullets, accent, href, testid }) => (
            <article
              key={title}
              data-testid={testid}
              className="group relative flex flex-col rounded-3xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-8 transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl hover:border-emerald-500/40"
            >
              <div className={`relative inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br ${accent} text-white shadow-lg`}>
                <Icon className="h-7 w-7" strokeWidth={2} />
              </div>

              <div className="mt-6">
                <div className="text-xs uppercase tracking-[0.15em] font-semibold text-slate-500 dark:text-slate-400">
                  {tagline}
                </div>
                <h3 className="font-display mt-2 text-2xl font-semibold text-slate-900 dark:text-white">
                  {title}
                </h3>
                <p className="mt-3 text-slate-600 dark:text-slate-300 leading-relaxed">
                  {desc}
                </p>
              </div>

              <ul className="mt-6 space-y-3 border-t border-slate-100 dark:border-slate-800 pt-6">
                {bullets.map((b) => (
                  <li key={b} className="flex items-start gap-3 text-sm text-slate-700 dark:text-slate-300">
                    <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-emerald-100 dark:bg-emerald-500/15 text-emerald-600 dark:text-emerald-400">
                      <Check className="h-3 w-3" strokeWidth={3} />
                    </span>
                    {b}
                  </li>
                ))}
              </ul>

              <Link
                to={href}
                data-testid={`${testid}-link`}
                className="mt-auto pt-8 inline-flex items-center gap-1 text-sm font-semibold text-emerald-600 dark:text-emerald-400 hover:gap-2 transition-all"
              >
                Läs mer <ArrowUpRight className="h-4 w-4" />
              </Link>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
